import { z } from "zod";
import { ItemSchema, ItemUnitSchema } from "./items";
import { LocationSchema } from "./locations";
import { HydratedOpportunitySchema } from "./opportunities";

export const DEMAND_FORECAST_CADENCE_VALUES = [
  "ONE_TIME",
  "WEEKLY",
  "MONTHLY",
  "QUARTERLY",
  "ANNUALLY",
] as const;

export const DemandForecastCadenceSchema = z.enum(
  DEMAND_FORECAST_CADENCE_VALUES,
);
export type DemandForecastCadence = z.infer<typeof DemandForecastCadenceSchema>;
export const DemandForecastCadenceEnum = DemandForecastCadenceSchema.enum;
export const DEMAND_FORECAST_CADENCE = DemandForecastCadenceSchema.enum;

export const DEMAND_FORECAST_CADENCE_DISPLAY_NAMES = {
  [DEMAND_FORECAST_CADENCE.ONE_TIME]: "One time",
  [DEMAND_FORECAST_CADENCE.WEEKLY]: "Weekly",
  [DEMAND_FORECAST_CADENCE.MONTHLY]: "Monthly",
  [DEMAND_FORECAST_CADENCE.QUARTERLY]: "Quarterly",
  [DEMAND_FORECAST_CADENCE.ANNUALLY]: "Annually",
} as const satisfies Record<DemandForecastCadence, string>;

const RequiredEntityIdSchema = z.string().trim().min(1);
const OptionalEntityIdSchema = RequiredEntityIdSchema.nullable().optional();

export const DemandForecastSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  opportunityId: z.string(),
  locationId: z.string().nullable(),
  itemId: z.string().nullable(),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  quantityPerPeriod: z.string(),
  quantityUnit: ItemUnitSchema,
  unitPrice: z.string(),
  cadence: DemandForecastCadenceSchema,
  startDate: z.coerce.date(),
  endDate: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DemandForecast = z.infer<typeof DemandForecastSchema>;

export const HydratedDemandForecastSchema = DemandForecastSchema.extend({
  opportunity: HydratedOpportunitySchema.optional(),
  location: LocationSchema.nullable().optional(),
  item: ItemSchema.nullable().optional(),
});
export type HydratedDemandForecast = z.infer<
  typeof HydratedDemandForecastSchema
>;

export const DemandForecastLineSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  demandForecastId: z.string(),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  quantity: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type DemandForecastLine = z.infer<typeof DemandForecastLineSchema>;

export const CreateDemandForecastParamsSchema = z.object({
  opportunityId: RequiredEntityIdSchema,
  locationId: OptionalEntityIdSchema,
  itemId: OptionalEntityIdSchema,
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  quantityPerPeriod: z.string(),
  quantityUnit: ItemUnitSchema,
  unitPrice: z.string(),
  cadence: DemandForecastCadenceSchema,
  startDate: z.coerce.date(),
  endDate: z.coerce.date().nullable().optional(),
});
export type CreateDemandForecastParams = z.infer<
  typeof CreateDemandForecastParamsSchema
>;

export const UpdateDemandForecastParamsSchema = z.object({
  opportunityId: RequiredEntityIdSchema.optional(),
  locationId: OptionalEntityIdSchema,
  itemId: OptionalEntityIdSchema,
  description: z.string().optional(),
  reasoning: z.string().nullable().optional(),
  quantityPerPeriod: z.string().optional(),
  quantityUnit: ItemUnitSchema.optional(),
  unitPrice: z.string().optional(),
  cadence: DemandForecastCadenceSchema.optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().nullable().optional(),
});
export type UpdateDemandForecastParams = z.infer<
  typeof UpdateDemandForecastParamsSchema
>;

export const DEFAULT_DEMAND_FORECAST_LIST_LIMIT = 50;

export const DemandForecastFilterSchema = z.object({
  search: z.string().optional(),
  opportunityId: RequiredEntityIdSchema.optional(),
  locationId: RequiredEntityIdSchema.optional(),
  itemId: RequiredEntityIdSchema.optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type DemandForecastFilter = z.infer<typeof DemandForecastFilterSchema>;

export const DemandForecastCalendarBucketSchema = z.enum([
  "WEEK",
  "MONTH",
  "QUARTER",
]);
export type DemandForecastCalendarBucket = z.infer<
  typeof DemandForecastCalendarBucketSchema
>;
export const DEMAND_FORECAST_CALENDAR_BUCKET =
  DemandForecastCalendarBucketSchema.enum;

export const DemandForecastCalendarFilterSchema = z.object({
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  locationId: RequiredEntityIdSchema.optional(),
  bucket: DemandForecastCalendarBucketSchema.default("MONTH"),
});
export type DemandForecastCalendarFilter = z.infer<
  typeof DemandForecastCalendarFilterSchema
>;

export const DemandForecastCalendarEntrySchema = z.object({
  lineId: z.string(),
  demandForecastId: z.string(),
  opportunityId: z.string(),
  opportunityName: z.string(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  itemId: z.string().nullable(),
  itemName: z.string().nullable(),
  description: z.string(),
  reasoning: z.string().nullable().optional(),
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  quantity: z.string(),
  weightedQuantity: z.string(),
  quantityUnit: ItemUnitSchema,
  unitPrice: z.string(),
  totalRevenue: z.string(),
  weightedRevenue: z.string(),
  probability: z.number(),
});
export type DemandForecastCalendarEntry = z.infer<
  typeof DemandForecastCalendarEntrySchema
>;

export const DemandForecastQuantityTotalSchema = z.object({
  quantityUnit: ItemUnitSchema,
  quantity: z.string(),
});
export type DemandForecastQuantityTotal = z.infer<
  typeof DemandForecastQuantityTotalSchema
>;

export const DemandForecastCalendarPeriodSchema = z.object({
  periodStart: z.coerce.date(),
  periodEnd: z.coerce.date(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  quantityTotals: z.array(DemandForecastQuantityTotalSchema),
  weightedQuantityTotals: z.array(DemandForecastQuantityTotalSchema),
  totalRevenue: z.string(),
  weightedRevenue: z.string(),
  entries: z.array(DemandForecastCalendarEntrySchema),
});
export type DemandForecastCalendarPeriod = z.infer<
  typeof DemandForecastCalendarPeriodSchema
>;

export const ListDemandForecastsResponseSchema = z.object({
  demandForecasts: z.array(HydratedDemandForecastSchema),
  total: z.number(),
});
export type ListDemandForecastsResponse = z.infer<
  typeof ListDemandForecastsResponseSchema
>;

export const GetDemandForecastResponseSchema = z.object({
  demandForecast: HydratedDemandForecastSchema,
});
export type GetDemandForecastResponse = z.infer<
  typeof GetDemandForecastResponseSchema
>;

export const CreateDemandForecastResponseSchema = z.object({
  demandForecast: HydratedDemandForecastSchema,
});
export type CreateDemandForecastResponse = z.infer<
  typeof CreateDemandForecastResponseSchema
>;

export const UpdateDemandForecastResponseSchema = z.object({
  demandForecast: HydratedDemandForecastSchema,
});
export type UpdateDemandForecastResponse = z.infer<
  typeof UpdateDemandForecastResponseSchema
>;

export const DeleteDemandForecastResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteDemandForecastResponse = z.infer<
  typeof DeleteDemandForecastResponseSchema
>;

export const DemandForecastCalendarResponseSchema = z.object({
  buckets: z.array(DemandForecastCalendarPeriodSchema),
});
export type DemandForecastCalendarResponse = z.infer<
  typeof DemandForecastCalendarResponseSchema
>;
